// Lobby messages
export type LobbyInvite = {
  kind: 'invite';
  matchId: string;
  from: { userId: string; userName: string };
  to:   { userId: string; userName: string };
};

export type LobbyInviteResponse = {
  kind: 'invite_response';
  matchId: string;
  to: { userId: string, userName: string }; // inviter userId
  from: { userId: string, userName: string }; // invitee userId
  accept: boolean;
};

export type LobbyInviteCancel = {
  kind: 'invite_cancel';
  from: { userId: string; userName: string };
  to: { userId: string; userName: string };
};

export type RestartRequest = {
  kind: 'restart_request';
  matchId: string;
  from: { userId: string; userName: string };
  to: { userId: string; userName: string };
};

export type RestartResponse = {
  kind: 'restart_response';
  matchId: string;
  to: { userId: string, userName: string }; // requester userId
  from: { userId: string, userName: string }; // responder userId
  accept: boolean;
};

// Re-exporting to break cache
export type RestartCancel = {
  kind: 'restart_cancel';
  matchId: string;
  from: { userId: string; userName: string };
  to: { userId: string; userName: string };
};

export type PlayerForfeit = {
  kind: 'player_forfeit';
  matchId: string;
  from: { userId: string; userName: string }; // player who quit
  to: { userId: string; userName: string }; // opponent who wins
};

export type MatchFoundMessage = {
  kind: 'match_found';
  matchId: string;
  from: { userId: string; userName: string; wins: number; losses: number; avgPPR: number };
  to: { userId: string; userName: string };
  startingPlayer: string; // userId of who starts
  isTournament?: boolean; // Flag to distinguish tournament matches from regular matchmaking
};

export type PlayerReady = {
  kind: 'player_ready';
  matchId: string;
  from: { userId: string; userName: string };
  to: { userId: string; userName: string };
};

export type MatchCancelled = {
  kind: 'match_cancelled';
  matchId: string;
  from: { userId: string; userName: string };
  to: { userId: string; userName: string };
  isTournament?: boolean;
};

// Observer messages
export type ObserverJoin = {
  kind: 'observer_join';
  matchId: string;
  from: { userId: string; userName: string };
};

export type ObserverLeave = {
  kind: 'observer_leave';
  matchId: string;
  from: { userId: string; userName: string };
};

export type ObserverList = {
  kind: 'observer_list';
  matchId: string;
  observers: Array<{ userId: string; userName: string }>;
};

// Room messages (match:<id>)
export type RoomHello = {
  kind: 'hello';
  matchId: string;
  userId: string;
  roundSeed: number; // base RNG seed for the whole match (optional)
};

export type StartRound = {
  kind: 'start_round';
  round: number;
  tiltX: number;      // per-round tilt (authoritative: P1 generates)
  seed: number;       // per-round seed (for any randomness you add later)
};

export type RollInput = {
  kind: 'roll_input';
  round: number;
  ballIndex: number;  // 0..3
  start: { x:number; y:number };
  velFracPerSec:     { x:number; y:number }; // flick velocity
  tiltX: number;
  spin:  number;                  // from estimateSpin
  t0:    number;                  // performance.now() origin (latency debugging)
};

export type TurnAck = {
  kind: 'turn_ack';
  round: number;
  ballIndex: number;
  userId: string;
};

export type RollResult =
  | { kind:'roll_result'; round:number; ballIndex:number; result:'hole'; holeId: number; pts:number; cx:number; cy:number; gameState: GameStateSync; rollerStats?: { wins: number; losses: number; avgPPR: number; } }
  | { kind:'roll_result'; round:number; ballIndex:number; result:'ditch'; gameState: GameStateSync; rollerStats?: { wins: number; losses: number; avgPPR: number; } }
  | { kind:'roll_result'; round:number; ballIndex:number; result:'stop'; gameState: GameStateSync; rollerStats?: { wins: number; losses: number; avgPPR: number; } }
  | { kind:'roll_result'; round:number; ballIndex:number; result:'timeout'; gameState: GameStateSync; rollerStats?: { wins: number; losses: number; avgPPR: number; } };

export type GameStateSync = {
  scores: [number, number];      // both players' scores
  ballsLeft: number;             // balls remaining in current turn
  currentPlayer: 0 | 1;          // whose turn it is (0 or 1)
  round: number;                 // current round
  turnHits: number[];           // points scored this turn
  isSkeechPossible?: boolean;    // whether a skeech is possible this turn
  skeechSuccess?: boolean;       // whether a skeech was just achieved
  wildcardHit?: number | null;   // wildcard hole value (duplicate or non-standard)
  phase?: 'normal' | 'over' | 'retribution' | 'sudden_death';  // current game phase
  suddenDeathScores?: [number, number];  // sudden death round scores
  suddenDeathRound?: number;     // sudden death round number
  lastRollPts?: number;          // points from the last roll (for tracking opponent rolls)
  lastRollResult?: 'hole' | 'ditch' | 'stop';  // result of the last roll (for passive player animations)
  lastRollPlayer?: 0 | 1;        // which player took the last roll
  player1PPR?: number;           // player 1's current PPR (updated after each roll)
  player2PPR?: number;           // player 2's current PPR (updated after each roll)
  completedTurnRolls?: number[]; // All rolls from the just-completed turn (for passive player history)
  bustHistory?: Array<{ player: 0 | 1; round: number }>;
  phaseHistory?: Array<{ phase: 'retribution' | 'sudden_death'; round: number; suddenDeathRound?: number }>;
};

export type ReadyForNext = {
  kind: 'ready_for_next';
  round: number;
  ballIndex: number;
  userId: string;
};

// Optional hash check to detect drift
export type StateHash = { kind:'hash'; round:number; h:string };

export type StateSync = {
  kind: 'state_sync';
  pos: { x: number; y: number };
  vel: { x: number; y: number };
  spin: number;
};

// Ball path recording for passive player smooth playback
export type BallSnapshot = {
  t: number;        // timestamp relative to roll start (seconds)
  x: number;
  y: number;
  vx: number;
  vy: number;
  spin: number;
  r?: number;       // ball radius (for bounce animation)
  bounceActive?: boolean;  // whether bounce animation is active
};

export type PathChunk = {
  kind: 'path_chunk';
  round: number;
  ballIndex: number;
  snapshots: BallSnapshot[];
  isComplete: boolean;  // true if this is the final chunk (ball stopped)
  finalGameState?: GameStateSync;  // Only present when isComplete=true
};

// Board message synchronization for passive player
export type BoardMessage = {
  kind: 'board_message';
  round: number;
  ballIndex: number;
  text: string;              // The message text to display
  messageData: any;          // Full message data (positioning, colors, effects, etc.)
  timestamp: number;         // Relative timestamp (seconds from roll start)
};

// Configurable constants for path batching
export const PATH_BATCH_CONFIG = {
  INITIAL_DELAY: 1.0,      // seconds before first batch (configurable: try 1.0-2.0)
  INTERVAL_SHRINK: 0.2,    // seconds to reduce each interval (configurable)
  MIN_BUFFER: 0.2,         // minimum runway buffer in seconds
};
